"use client";

import { Button, SubmitButton } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { PlanIcon } from "@/components/ui/plan-icon";

interface WizardFooterProps {
  currentStep: number;
  totalSteps: number;
  onBack: () => void;
  onNext: () => void;
  canContinue: boolean;
  submitting: boolean;
}

const PLAN_STEP = 5;

/** Free-plan wash — same stop used on billing current-plan / status surfaces. */
const FREE_PLAN_GRADIENT =
  "linear-gradient(to top, rgba(145, 248, 136, 0.08), transparent 80%)";

export function WizardFooter({
  currentStep,
  totalSteps,
  onBack,
  onNext,
  canContinue,
  submitting,
}: WizardFooterProps) {
  const isPlanStep = currentStep === PLAN_STEP;

  if (isPlanStep) {
    return (
      <div className="relative flex w-full shrink-0 items-center gap-3 overflow-hidden rounded-lg border border-border p-3 dark:border-white/10">
        <div
          className="pointer-events-none absolute inset-0 rounded-lg"
          style={{ background: FREE_PLAN_GRADIENT }}
        />

        <div className="relative flex items-center gap-2">
          <Button
            onClick={onBack}
            disabled={submitting}
            variant="ghost"
            size="sm"
            aria-label="Back"
            className="shrink-0 size-9"
            icon={<Icon name="IconArrowLeft" size={20} />}
          />

          <div className="flex size-9 shrink-0 items-center justify-center rounded-md border border-border bg-contrast/4 dark:border-white/5 dark:bg-white/4">
            <PlanIcon slug="free" width={20} height={20} className="size-5" />
          </div>
        </div>

        <p className="relative min-w-0 flex-1 text-sm font-medium leading-5 text-heading dark:text-white">
          Continue for free and see how Arbi works
        </p>

        <SubmitButton
          type="button"
          onClick={onNext}
          loading={submitting}
          loadingLabel="Continuing"
          variant="secondary"
          size="sm"
          className="relative shrink-0"
        >
          Continue
        </SubmitButton>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-2">
        {currentStep > 1 && (
          <Button
            onClick={onBack}
            variant="secondary"
            size="sm"
            className="shrink-0 bg-transparent border-contrast/8 hover:border-contrast/[0.15] text-contrast/50 hover:text-contrast/70 shadow-none"
            icon={<Icon name="IconArrowLeft" size={14} />}
          >
            Back
          </Button>
        )}
      </div>

      <SubmitButton
        type="button"
        onClick={onNext}
        loading={submitting}
        loadingLabel="Continuing"
        disabled={!canContinue}
        variant="primary"
        size="sm"
        className="shrink-0"
        icon={<Icon name="IconArrowRight" size={16} />}
      >
        Continue
      </SubmitButton>
    </div>
  );
}
